import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import axiosInstance from "../../../services/AxiosInstance";
import Api from "../../../constants/Api";
import PageTitle from "../../layouts/PageTitle";
import LoadingCenter from "../../components/Common/LoadingCenter";
import { ApiError } from "../../components/Common/ApiError";
import EmployeeSelect from "../../components/Custom/Main/Employee/EmployeeSelect";
import CategorySelect from "../../components/Custom/Main/Category/CategorySelect";
import ReportExportButtons from "../../components/Common/Report/ReportExportButtons";
import { useBranch } from "../../../context/BranchContext";
import { convertDoubleToStringPTBR } from "../../../utils/ConverterUtils";
import './EmployeeStatementReport.css';

/** Os mesmos atalhos do Extrato do Colaborador: as duas telas são conferidas no mesmo ritmo. */
const QUICK_PERIODS = {
    this_month: () => [moment().startOf('month'), moment().endOf('month')],
    last_month: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
    last_7_days: () => [moment().subtract(6, 'days'), moment()],
};

const toApiDate = (date) => date.format('YYYY-MM-DD');

const formatCurrency = (value) => `R$ ${convertDoubleToStringPTBR(Number(value || 0))}`;

/** Quantidade fracionária existe para serviço vendido por fração, então só mostra decimal quando há. */
const formatQuantity = (value) => {
    const amount = Number(value || 0);
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace('.', ',');
};

const SalesReport = () => {

    const { t } = useTranslation();

    // A filial ativa vem exclusivamente do cabeçalho/contexto, igual ao Extrato do
    // Colaborador: assim o relatório nunca consulta uma filial diferente da exibida.
    const { selectedBranch } = useBranch();
    const branchId = selectedBranch?.id ?? selectedBranch?.value ?? null;
    const [employeeFilter, setEmployeeFilter] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState([]);

    const [period, setPeriod] = useState(() => {
        const [start, end] = QUICK_PERIODS.this_month();
        return { startDate: toApiDate(start), endDate: toApiDate(end) };
    });
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    // Colaborador aberto no detalhamento. Um por vez, como no Extrato: o gestor confere
    // um caso de cada vez depois de comparar a lista.
    const [expandedKey, setExpandedKey] = useState(null);
    const reportRef = useRef(null);
    const detailPrintRef = useRef(null);

    const invalidPeriod = moment(period.startDate).isAfter(moment(period.endDate));

    const loadRows = useCallback(() => {
        if (invalidPeriod) return;

        setLoading(true);
        // Trocar o período invalida o detalhe aberto: ele é sempre do mesmo intervalo da lista.
        setExpandedKey(null);

        axiosInstance.get(Api.reports.extra_services, {
            params: {
                ...period,
                branchId,
                // A API recebe uma lista separada por vírgulas para manter a chamada
                // compatível com clientes que ainda enviam o filtro categoryId único.
                categoryIds: categoryFilter.length > 0
                    ? categoryFilter.map(option => option.value).join(',')
                    : null
            }
        })
            .then((resp) => setRows(resp.data || []))
            .catch(error => ApiError(error))
            .finally(() => setLoading(false));
    }, [period, branchId, invalidPeriod, categoryFilter]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        // A troca feita no cabeçalho pode tornar o colaborador selecionado inválido
        // para a nova filial, então o filtro dependente precisa ser limpo.
        setEmployeeFilter(null);
    }, [branchId]);

    const visibleRows = rows.filter(row => !employeeFilter || row.employeeId === employeeFilter.value);

    /**
     * A consulta volta uma linha por serviço; a tela é por colaborador. Agrupa por filial e
     * colaborador mantendo a ordem alfabética das duas, que é como a lista é conferida.
     */
    /**
     * Soma uma linha num agrupamento (colaborador, filial ou total). O faturamento é o que foi
     * cobrado; "coberto" é o atendido sem cobrança no balcão (assinatura, cortesia), que fica
     * separado para o faturamento bater com o DRE sem esconder a produção.
     */
    const addAmounts = (target, source) => {
        target.quantity += Number(source.quantity || 0);
        target.revenue += Number(source.revenue || 0);
        target.covered += Number(source.coveredValue ?? source.covered ?? 0);
    };

    const branches = visibleRows.reduce((result, row) => {
        let branch = result.find(item => item.branchId === row.branchId);
        if (!branch) {
            branch = { branchId: row.branchId, branchName: row.branchName || t('all_branches'), employees: [], quantity: 0, revenue: 0, covered: 0 };
            result.push(branch);
        }
        let employee = branch.employees.find(item => item.employeeId === row.employeeId);
        if (!employee) {
            // Venda de balcão pode ser lançada sem profissional. A linha existe e soma no
            // faturamento, então aparece nomeada em vez de sumir do relatório.
            employee = {
                employeeId: row.employeeId,
                employeeName: row.employeeName || 'Sem colaborador',
                services: [], quantity: 0, revenue: 0, covered: 0
            };
            branch.employees.push(employee);
        }
        employee.services.push(row);
        addAmounts(employee, row);
        addAmounts(branch, row);
        return result;
    }, []).sort((first, second) => (first.branchName || '').localeCompare(second.branchName || '', 'pt-BR'));

    branches.forEach(branch => {
        branch.employees.sort((first, second) => Number(second.revenue || 0) - Number(first.revenue || 0));
        // Depois da ordenação por faturamento o maior é o primeiro, e é ele a régua da
        // barra de proporção da coluna de faturamento.
        branch.maxRevenue = Number(branch.employees[0]?.revenue || 0);
        branch.employees.forEach(employee => employee.services.sort((first, second) =>
            (first.serviceName || '').localeCompare(second.serviceName || '', 'pt-BR')));
    });

    const totals = branches.reduce((acc, branch) => {
        addAmounts(acc, branch);
        return acc;
    }, { quantity: 0, revenue: 0, covered: 0 });

    const hasRows = branches.length > 0;

    const applyQuickPeriod = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        setPeriod({ startDate: toApiDate(start), endDate: toApiDate(end) });
    };

    const isQuickPeriodActive = (key) => {
        const [start, end] = QUICK_PERIODS[key]();
        return period.startDate === toApiDate(start) && period.endDate === toApiDate(end);
    };

    /** Chave da linha expandida: o mesmo colaborador pode aparecer em mais de uma filial. */
    const rowKey = (branch, employee) => `${branch.branchId}-${employee.employeeId}`;

    const exportHeaders = [t('branch'), t('employee'), t('sales_report_items_sold'), t('sales_report_revenue'), t('sales_report_covered')];
    const exportRows = branches.flatMap(branch => [
        ...branch.employees.map(employee => [
            branch.branchName,
            employee.employeeName,
            formatQuantity(employee.quantity),
            formatCurrency(employee.revenue),
            formatCurrency(employee.covered)
        ]),
        [branch.branchName, `${t('total')} ${branch.branchName}`, formatQuantity(branch.quantity), formatCurrency(branch.revenue), formatCurrency(branch.covered)]
    ]);

    if (hasRows) {
        exportRows.push(['', 'Total geral', formatQuantity(totals.quantity), formatCurrency(totals.revenue), formatCurrency(totals.covered)]);
    }

    // Renderizado dentro do próprio tbody, logo abaixo da linha do colaborador: o detalhe
    // precisa ficar junto de quem ele descreve, senão o gestor perde a referência ao rolar.
    const renderDetailRow = (branch, employee, columnCount) => (
        <tr className="employee-statement__detail-row">
            <td colSpan={columnCount}>
                <div ref={detailPrintRef} className="employee-statement__detail">
                    <div className="employee-statement__detail-actions d-print-none">
                        <ReportExportButtons
                            contentRef={detailPrintRef}
                            documentTitle={`servicos-${employee.employeeName}`}
                            fileName={`servicos-${employee.employeeName}`}
                            preambleRows={[
                                [t('employee'), employee.employeeName],
                                [t('branch'), branch.branchName],
                                ['Período', `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`]
                            ]}
                            headers={['Produto/serviço', t('sales_report_items_sold'), t('sales_report_revenue'), t('sales_report_covered')]}
                            rows={employee.services.map(service => [
                                service.serviceName,
                                formatQuantity(service.quantity),
                                formatCurrency(service.revenue),
                                formatCurrency(service.coveredValue)
                            ])}
                        />
                    </div>

                    <div className="employee-statement__detail-export-title">
                        <h1>{t('sales_report')}</h1>
                        <p>{t('employee')}: {employee.employeeName}</p>
                        <p>{t('branch')}: {branch.branchName}</p>
                        <p>Período: {moment(period.startDate).format('DD/MM/YYYY')} a {moment(period.endDate).format('DD/MM/YYYY')}</p>
                    </div>

                    <h5 className="employee-statement__section-title">Produtos e serviços</h5>
                    <div className="table-responsive">
                        <Table striped size="sm">
                            <thead>
                                <tr>
                                    <th>Produto/serviço</th>
                                    <th className="text-end">{t('sales_report_items_sold')}</th>
                                    <th className="text-end employee-statement__cell--emphasis">{t('sales_report_revenue')}</th>
                                    <th className="text-end">{t('sales_report_covered')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employee.services.map(service => (
                                    <tr key={service.serviceId}>
                                        <td>{service.serviceName}</td>
                                        <td className="text-end">{formatQuantity(service.quantity)}</td>
                                        {/* Atendimento coberto pelo plano sai por R$ 0,00 no faturamento, e o
                                            valor dele aparece na coluna ao lado. O zero é o valor real da
                                            linha e soma com o total; traço pareceria dado faltando. */}
                                        <td className="text-end">{formatCurrency(service.revenue)}</td>
                                        <td className="text-end">{formatCurrency(service.coveredValue)}</td>
                                    </tr>
                                ))}
                                {employee.services.length === 0 && (
                                    <tr><td colSpan={4} className="text-center py-3">{t('message.no_records_found')}</td></tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <th>{t('total')}</th>
                                    <th className="text-end">{formatQuantity(employee.quantity)}</th>
                                    <th className="text-end">{formatCurrency(employee.revenue)}</th>
                                    <th className="text-end">{formatCurrency(employee.covered)}</th>
                                </tr>
                            </tfoot>
                        </Table>
                    </div>
                </div>
            </td>
        </tr>
    );

    /** Versão só para impressão/PDF: uma seção por filial, sem as linhas expansíveis. */
    const renderPrintableReport = () => {
        const columns = (
            <thead>
                <tr>
                    <th>{t('employee')}</th>
                    <th className="text-end">{t('sales_report_items_sold')}</th>
                    <th className="text-end print-emphasis">{t('sales_report_revenue')}</th>
                    <th className="text-end">{t('sales_report_covered')}</th>
                </tr>
            </thead>
        );

        return (
            <div className="employee-statement__print-report">
                <h1>{t('sales_report')}</h1>
                <p>Período: {moment(period.startDate).format('DD/MM/YYYY')} a {moment(period.endDate).format('DD/MM/YYYY')}</p>
                <p>{t('sales_report_revenue')}: {t('sales_report_revenue_hint')}</p>
                <p>{t('sales_report_covered')}: {t('sales_report_covered_hint')}</p>
                {employeeFilter && <p>{t('employee')}: {employeeFilter.label}</p>}

                {branches.map(branch => (
                    <section className="employee-statement__print-branch" key={branch.branchId ?? 'all'}>
                        <h2>{branch.branchName}</h2>
                        <Table bordered striped size="sm">
                            {columns}
                            <tbody>
                                {branch.employees.map(employee => (
                                    <tr key={rowKey(branch, employee)}>
                                        <td>{employee.employeeName}</td>
                                        <td className="text-end">{formatQuantity(employee.quantity)}</td>
                                        <td className="text-end print-emphasis">{formatCurrency(employee.revenue)}</td>
                                        <td className="text-end">{formatCurrency(employee.covered)}</td>
                                    </tr>
                                ))}
                                <tr>
                                    <th>{t('total')} {branch.branchName}</th>
                                    <th className="text-end">{formatQuantity(branch.quantity)}</th>
                                    <th className="text-end print-emphasis">{formatCurrency(branch.revenue)}</th>
                                    <th className="text-end">{formatCurrency(branch.covered)}</th>
                                </tr>
                            </tbody>
                        </Table>
                    </section>
                ))}

                <h2 className="employee-statement__print-total-title">{t('total')}</h2>
                <Table bordered size="sm">
                    {columns}
                    <tbody>
                        <tr>
                            <th>Total geral</th>
                            <th className="text-end">{formatQuantity(totals.quantity)}</th>
                            <th className="text-end print-emphasis">{formatCurrency(totals.revenue)}</th>
                            <th className="text-end">{formatCurrency(totals.covered)}</th>
                        </tr>
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <>
            <PageTitle activeMenu={t('sales_report')} motherMenu={t('reports')} />

            <p className="employee-statement__subtitle">{t('sales_report_hint')}</p>


            <div className="employee-statement__filters">
                <Row className="g-3 align-items-end">
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_start')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.startDate}
                                onChange={(e) => setPeriod(prev => ({ ...prev, startDate: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={3} md={6}>
                        <Form.Group>
                            <Form.Label>{t('date_end')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={period.endDate}
                                onChange={(e) => setPeriod(prev => ({ ...prev, endDate: e.target.value }))}
                            />
                        </Form.Group>
                    </Col>
                    <Col xl={6} md={6}>
                        <Form.Group>
                            <Form.Label>{t('category')}</Form.Label>
                    <CategorySelect
                        isMulti
                        value={categoryFilter}
                        onChange={(options) => { setCategoryFilter(options || []); setExpandedKey(null); }}
                    />
                        </Form.Group>
                    </Col>
                    {/* Mantém o colaborador em uma segunda linha para não comprimir o multiselect. */}
                    <div className="w-100" />
                    <Col xl={4} md={6}>
                        <Form.Group>
                            <Form.Label>{t('employee')}</Form.Label>
                            <EmployeeSelect
                                branch={selectedBranch}
                                value={employeeFilter}
                                onChange={(option) => {
                                    setEmployeeFilter(option);
                                    setExpandedKey(null);
                                }}
                                filterProvidesService={false}
                            />
                        </Form.Group>
                    </Col>
                    <Col xs={12}>
                        <div className="employee-statement__quick-periods">
                            {Object.keys(QUICK_PERIODS).map(key => (
                                <Button
                                    key={key}
                                    size="sm"
                                    variant={isQuickPeriodActive(key) ? 'primary' : 'outline-primary'}
                                    onClick={() => applyQuickPeriod(key)}
                                >
                                    {t(`period_shortcut.${key}`)}
                                </Button>
                            ))}
                        </div>
                    </Col>
                </Row>

                {invalidPeriod && (
                    <div className="employee-statement__warning">
                        <i className="fas fa-triangle-exclamation me-2"></i>
                        {t('validation.period_start_after_end')}
                    </div>
                )}

                <div className="employee-statement__exports">
                    <ReportExportButtons
                        contentRef={reportRef}
                        headers={exportHeaders}
                        rows={exportRows}
                        preambleRows={[
                            [t('sales_report')],
                            ['Período', `${moment(period.startDate).format('DD/MM/YYYY')} a ${moment(period.endDate).format('DD/MM/YYYY')}`],
                            ...(employeeFilter ? [[t('employee'), employeeFilter.label]] : [])
                        ]}
                        fileName="servicos-realizados"
                        documentTitle="servicos-realizados"
                        disabled={!hasRows}
                    />
                </div>
            </div>

            {loading ? (
                <LoadingCenter className="mt-5" />
            ) : (
                <div className={`table-responsive employee-statement__table${expandedKey ? ' has-detail' : ''}`}>
                    <Table>
                        {/* Larguras declaradas junto com table-layout: fixed. Sem isso, a tabela
                            do detalhe expandido reabre o cálculo de largura da tabela externa e
                            as colunas dançam a cada linha aberta. */}
                        <colgroup>
                            <col style={{ width: '42%' }} />
                            <col style={{ width: '15%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '18%' }} />
                            <col style={{ width: '5%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                {/* Sem coluna de filial: a linha de cabeçalho de cada grupo já
                                    diz de qual unidade são as linhas abaixo dela. */}
                                <th className="employee-statement__col-name">{t('employee')}</th>
                                <th className="text-end" title={t('sales_report_items_sold_hint')}>
                                    {t('sales_report_items_sold')}
                                </th>
                                <th className="text-end employee-statement__cell--emphasis" title={t('sales_report_revenue_hint')}>
                                    {t('sales_report_revenue')}
                                </th>
                                <th className="text-end" title={t('sales_report_covered_hint')}>
                                    {t('sales_report_covered')}
                                </th>
                                <th aria-label={t('details')}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {branches.map((branch, branchIndex) => (
                                <Fragment key={branch.branchId ?? 'all'}>
                                    {/* Linha vazia entre os grupos. Sem esse respiro o total de uma
                                        filial encosta no cabeçalho da seguinte e as duas viram
                                        um bloco só na leitura. */}
                                    {branchIndex > 0 && (
                                        <tr className="employee-statement__branch-gap" aria-hidden="true">
                                            <td colSpan={5}></td>
                                        </tr>
                                    )}
                                    <tr className="employee-statement__branch-row">
                                        <th colSpan={5}>
                                            <div className="employee-statement__branch-heading">
                                                <span>
                                                    <i className="fas fa-building me-2"></i>{branch.branchName}
                                                </span>
                                                <small>
                                                    {branch.employees.length} {t('employee')} · {formatCurrency(branch.revenue)} vendidos
                                                </small>
                                            </div>
                                        </th>
                                    </tr>
                                    {branch.employees.map((employee, index) => {
                                        const key = rowKey(branch, employee);
                                        const isOpen = expandedKey === key;
                                        return (
                                            <Fragment key={key}>
                                                <tr
                                                    className={`employee-statement__row ${index % 2 === 0 ? 'is-odd' : ''} ${isOpen ? 'is-open' : ''}`}
                                                    onClick={() => setExpandedKey(isOpen ? null : key)}
                                                >
                                                    <td className="employee-statement__col-name">{employee.employeeName}</td>
                                                    <td className="text-end">{formatQuantity(employee.quantity)}</td>
                                                    {/* A barra compara com o maior faturamento da própria filial:
                                                        mostra quem puxa o resultado sem ler valor por valor. */}
                                                    <td className="text-end employee-statement__cell--emphasis employee-statement__cell--bar">
                                                        <span
                                                            className="employee-statement__bar"
                                                            style={{ width: `${branch.maxRevenue > 0
                                                                ? Math.round((Number(employee.revenue || 0) / branch.maxRevenue) * 100)
                                                                : 0}%` }}
                                                            aria-hidden="true"
                                                        />
                                                        {formatCurrency(employee.revenue)}
                                                    </td>
                                                    <td className="text-end">{formatCurrency(employee.covered)}</td>
                                                    <td className="text-end">
                                                        <i className={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                                    </td>
                                                </tr>
                                                {isOpen && renderDetailRow(branch, employee, 5)}
                                            </Fragment>
                                        );
                                    })}
                                    <tr className="employee-statement__branch-total-row">
                                        <td className="employee-statement__col-name"><strong>{t('total')} {branch.branchName}</strong></td>
                                        <td className="text-end">{formatQuantity(branch.quantity)}</td>
                                        <td className="text-end employee-statement__cell--emphasis"><strong>{formatCurrency(branch.revenue)}</strong></td>
                                        <td className="text-end">{formatCurrency(branch.covered)}</td>
                                        <td></td>
                                    </tr>
                                </Fragment>
                            ))}
                            {!hasRows && (
                                <tr>
                                    <td colSpan={5} className="text-center py-4">
                                        {t('message.no_records_found')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {hasRows && (
                            <tfoot>
                                <tr>
                                    <td className="employee-statement__col-name"><strong>Total geral</strong></td>
                                    <td className="text-end"><strong>{formatQuantity(totals.quantity)}</strong></td>
                                    <td className="text-end employee-statement__cell--emphasis"><strong>{formatCurrency(totals.revenue)}</strong></td>
                                    <td className="text-end"><strong>{formatCurrency(totals.covered)}</strong></td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </div>
            )}

            <div ref={reportRef} className="employee-statement__print-source" aria-hidden="true">
                {renderPrintableReport()}
            </div>

        </>
    );
};

export default SalesReport;
